import SelectionPinLayer from '../layers/SelectionPinLayer';
import { BDOT10K_LAYER_LABELS, OVERLAY_LAYERS_MIN_ZOOM } from '../core/constants';
import { centroidOfGeometry, nearestPointOnGeometry, haversineDistance, midpoint } from '../utils/coords';
import { bdot10kImpactBalanceLabel, bdot10kImpactHelp, bdot10kImpactSummary, bdot10kLabel, bdot10kLayerLabel, bdot10kSpatialImpactSummary, esc, formatDist, renderInfoHelpButton } from '../utils/formatters';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { addOrUpdateGeoJsonSource, removeSourceAndLayers, registerCursorHandlers, setVisibility } from '../utils/maplibre-helpers';
import { createMapHighlightPulsator, getMapHighlightPaint } from '../utils/map-highlight';
import { buildNearbyFamilies, getNearbyFamilyKey, levelRangeLabel, nearestFamilyEntry, resolveAdjacentFamilyKey, sourceElementsLabel } from '../utils/nearby-groups';
import { buildPowerLineSafetyHtml, getPowerLineSafetyBufferMeters } from '../utils/power-line-safety';
import { buildAirportCorridorNoteHtml } from '../utils/airport-corridor';
import { geoportalMapviewUrl } from '../utils/url';

var HIGHLIGHT_LAYER_IDS = [
    'lyr-nearby-highlight-fill',
    'lyr-nearby-highlight-polygon-casing',
    'lyr-nearby-highlight-line',
    'lyr-nearby-highlight-linestring-casing',
    'lyr-nearby-highlight-linestring',
    'lyr-nearby-highlight-circle'
];
var POWER_LINE_BUFFER_SOURCE_ID = 'src-nearby-power-line-buffer';
var POWER_LINE_BUFFER_LAYER_IDS = [
    'lyr-nearby-power-line-buffer-fill',
    'lyr-nearby-power-line-buffer-line'
];

export function createNearbyManager(config) {
    var state = config.state;
    var interactionsBound = false;
    var activeTabKey = null;
    var cachedTabs = [];
    var loadedForLatLng = null;
    var messages = config.messages || {};
    var powerLineBufferRequestVersion = 0;
    var powerLinePopup = null;
    var powerLineContextOriginalEvent = null;
    var highlightPulsator = createMapHighlightPulsator({
        getMap: config.getMap,
        layerIds: {
            fill: 'lyr-nearby-highlight-fill',
            polygonCasing: 'lyr-nearby-highlight-polygon-casing',
            polygonLine: 'lyr-nearby-highlight-line',
            lineStringCasing: 'lyr-nearby-highlight-linestring-casing',
            lineString: 'lyr-nearby-highlight-linestring',
            circle: 'lyr-nearby-highlight-circle'
        }
    });

    function getMessage(key, fallback) {
        return messages[key] || fallback;
    }

    function escAttr(value) {
        return esc(value).replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }

    function familyCategoriesLabel(count) {
        var lastTwo = count % 100;
        var last = count % 10;
        var noun = count === 1 ? 'kategoria otoczenia' : (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'kategorie otoczenia' : 'kategorii otoczenia');
        return count + ' ' + noun;
    }

    function midpointLatLng(a, b) {
        return midpoint(a, b);
    }

    function clearVisuals() {
        highlightPulsator.stop();
        clearPowerLineBuffer();
        var abortNearby = state.getAbortNearby();
        if (abortNearby) {
            abortNearby.abort();
            state.setAbortNearby(null);
        }

        var map = config.getMap();
        var nearbyLayer = state.getNearbyLayer();
        if (nearbyLayer && map) {
            removeSourceAndLayers(map, 'src-nearby', ['lyr-nearby-circle', 'lyr-nearby-fill', 'lyr-nearby-line', 'lyr-nearby-linestring'].concat(HIGHLIGHT_LAYER_IDS));
        }

        var nearbyLinkLayer = state.getNearbyLinkLayer();
        if (nearbyLinkLayer && map) {
            if (nearbyLinkLayer.markerGroup) nearbyLinkLayer.markerGroup.remove();
            removeSourceAndLayers(map, 'src-nearby-link', ['lyr-nearby-link-casing', 'lyr-nearby-link-line']);
        }

        config.clearSelectionPin();
        state.setNearbyLayer(null);
        state.setNearbyLinkLayer(null);
    }

    function clearPowerLineBuffer() {
        powerLineBufferRequestVersion += 1;
        var map = config.getMap();
        if (map) {
            removeSourceAndLayers(map, POWER_LINE_BUFFER_SOURCE_ID, POWER_LINE_BUFFER_LAYER_IDS);
        }
        if (powerLinePopup) {
            powerLinePopup.remove();
        }
    }

    function getPowerLineBufferBbox(item) {
        var selected = state.getSelectedLatLng();
        var target = item ? (item.nearest || item.center) : null;
        if (!selected || !target) return null;

        var lonPadding = Math.max(Math.abs(selected.lng - target.lng) * 0.25, 0.003);
        var latPadding = Math.max(Math.abs(selected.lat - target.lat) * 0.25, 0.003);
        return [
            Math.min(selected.lng, target.lng) - lonPadding,
            Math.min(selected.lat, target.lat) - latPadding,
            Math.max(selected.lng, target.lng) + lonPadding,
            Math.max(selected.lat, target.lat) + latPadding
        ];
    }

    function renderPowerLineBuffer(item, feature) {
        var map = config.getMap();
        if (!map || !item || !feature || !feature.geometry) return;

        removeSourceAndLayers(map, POWER_LINE_BUFFER_SOURCE_ID, POWER_LINE_BUFFER_LAYER_IDS);
        feature.properties = Object.assign({}, feature.properties || {}, {
            bdot10k_id: item.bdot10k_id,
            detail_url: item.detail_url,
            label: item.label,
            rodzaj: item.rodzaj,
            power_line_safety_buffer: true,
            power_line_buffer_m: getPowerLineSafetyBufferMeters(item.rodzaj)
        });
        addOrUpdateGeoJsonSource(map, POWER_LINE_BUFFER_SOURCE_ID, {
            type: 'FeatureCollection',
            features: [feature]
        });
        map.addLayer({
            id: POWER_LINE_BUFFER_LAYER_IDS[0], type: 'fill', source: POWER_LINE_BUFFER_SOURCE_ID,
            paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.18 }
        });
        map.addLayer({
            id: POWER_LINE_BUFFER_LAYER_IDS[1], type: 'line', source: POWER_LINE_BUFFER_SOURCE_ID,
            paint: {
                'line-color': '#b45309',
                'line-width': 1.5,
                'line-dasharray': [3, 2],
                'line-opacity': 0.9
            }
        });
        applyLayerVisibility();
        if (typeof config.bringDataLayersToFront === 'function') {
            config.bringDataLayersToFront();
        }
    }

    function loadPowerLineBuffer(item) {
        clearPowerLineBuffer();
        var bufferMeters = item ? getPowerLineSafetyBufferMeters(item.rodzaj) : null;
        var bbox = getPowerLineBufferBbox(item);
        if (!item || !item.bdot10k_id || !bufferMeters || !bbox) return;

        var requestVersion = powerLineBufferRequestVersion;
        var url = 'api/bdot10k-szczegoly-map/' + encodeURIComponent(item.bdot10k_id)
            + '?safety_buffer_bbox=' + encodeURIComponent(bbox.map(function(value) { return value.toFixed(6); }).join(','));
        fetch(url)
            .then(function(response) { return response.json(); })
            .then(function(data) {
                if (requestVersion !== powerLineBufferRequestVersion) return;
                if (String(state.getActiveNearbyId()) !== String(item.bdot10k_id)) return;
                if (data && data.safetyBuffer) renderPowerLineBuffer(item, data.safetyBuffer);
            })
            .catch(function() {
                // The selected line remains usable when the optional buffer cannot be loaded.
            });
    }

    function showPowerLineBufferPopup(event) {
        var map = config.getMap();
        var feature = event && event.features ? event.features[0] : null;
        var props = feature && feature.properties ? feature.properties : {};
        if (!map || !feature || !event.lngLat) return;

        if (!powerLinePopup) {
            powerLinePopup = new maplibregl.Popup({ maxWidth: '380px', closeButton: true });
        }
        powerLinePopup
            .setLngLat([event.lngLat.lng, event.lngLat.lat])
            .setHTML(buildPowerLineSafetyHtml(props, props.label || 'Linia elektroenergetyczna', esc))
            .addTo(map);
    }

    function getRenderedPowerLineBufferFeature(point) {
        var map = config.getMap();
        if (!map || !point || !map.getLayer(POWER_LINE_BUFFER_LAYER_IDS[0])) return null;
        var features = map.queryRenderedFeatures(point, { layers: [POWER_LINE_BUFFER_LAYER_IDS[0]] });
        return features && features.length ? features[0] : null;
    }

    function findNearbyItemByFeature(feature) {
        var id = feature && feature.properties ? feature.properties.bdot10k_id : null;
        if (id == null) return null;
        return state.getNearbyItems().find(function(entry) {
            return String(entry.bdot10k_id) === String(id);
        }) || null;
    }

    function getOverlappingNearbyItem(point, bufferId, preferredFeature) {
        var preferredItem = findNearbyItemByFeature(preferredFeature);
        if (preferredItem && String(preferredItem.bdot10k_id) !== String(bufferId)) {
            return preferredItem;
        }

        var map = config.getMap();
        if (!map || !point) return null;
        var nearbyLayerIds = ['lyr-nearby-fill', 'lyr-nearby-line', 'lyr-nearby-linestring', 'lyr-nearby-circle']
            .filter(function(layerId) { return !!map.getLayer(layerId); });
        if (!nearbyLayerIds.length) return null;

        var rendered = map.queryRenderedFeatures(point, { layers: nearbyLayerIds });
        for (var i = 0; i < rendered.length; i += 1) {
            var item = findNearbyItemByFeature(rendered[i]);
            if (item && String(item.bdot10k_id) !== String(bufferId)) return item;
        }
        return null;
    }

    function showPowerLineOverlapActions(event, bufferFeature, item) {
        var map = config.getMap();
        var bufferProps = bufferFeature && bufferFeature.properties ? bufferFeature.properties : {};
        var bufferMeters = Number(bufferProps.power_line_buffer_m) || getPowerLineSafetyBufferMeters(bufferProps.rodzaj) || 0;
        if (!map || !event.lngLat || !item) return;

        if (!powerLinePopup) {
            powerLinePopup = new maplibregl.Popup({ maxWidth: '380px', closeButton: true });
        }
        powerLinePopup
            .setLngLat([event.lngLat.lng, event.lngLat.lat])
            .setHTML(
                '<div class="nm-popup-card nm-popup-card--power-line-warning">'
                    + '<div class="nm-popup-kicker">Nakładające się informacje</div>'
                    + '<div class="nm-popup-title">Co chcesz otworzyć?</div>'
                    + '<p class="nm-popup-copy">W tym miejscu znajduje się orientacyjny pas linii energetycznej oraz inny obiekt z otoczenia.</p>'
                    + '<div class="nm-popup-actions">'
                        + '<button class="nm-popup-action nm-popup-action--warning" type="button" data-power-line-context-action="buffer">Informacja o pasie ' + esc(bufferMeters) + ' metrów</button>'
                        + '<button class="nm-popup-action" type="button" data-power-line-context-action="object">Wybierz obiekt: ' + esc(item.label || 'obiekt z otoczenia') + '</button>'
                    + '</div>'
                + '</div>'
            )
            .addTo(map);

        var popupElement = typeof powerLinePopup.getElement === 'function' ? powerLinePopup.getElement() : null;
        if (!popupElement) return;
        var bufferButton = popupElement.querySelector('[data-power-line-context-action="buffer"]');
        var objectButton = popupElement.querySelector('[data-power-line-context-action="object"]');
        if (bufferButton) {
            bufferButton.addEventListener('click', function() {
                powerLinePopup.setHTML(buildPowerLineSafetyHtml(
                    bufferProps,
                    bufferProps.label || 'Linia elektroenergetyczna',
                    esc
                ));
            });
        }
        if (objectButton) {
            objectButton.addEventListener('click', function() {
                powerLinePopup.remove();
                focusItem(item, { fly: false });
            });
        }
    }

    function handlePowerLineBufferOverlap(event, preferredFeature) {
        if (!event || !event.point) return false;
        var bufferFeature = getRenderedPowerLineBufferFeature(event.point);
        if (!bufferFeature) return false;

        if (event.originalEvent && powerLineContextOriginalEvent === event.originalEvent) {
            return true;
        }
        powerLineContextOriginalEvent = event.originalEvent || null;
        config.consumeHandledMapClick(event);
        event.preventDefault();
        if (event.originalEvent && typeof event.originalEvent.stopPropagation === 'function') {
            event.originalEvent.stopPropagation();
        }

        var bufferId = bufferFeature.properties ? bufferFeature.properties.bdot10k_id : null;
        var overlappingItem = getOverlappingNearbyItem(event.point, bufferId, preferredFeature);
        if (overlappingItem) {
            showPowerLineOverlapActions(event, bufferFeature, overlappingItem);
        } else {
            showPowerLineBufferPopup({
                features: [bufferFeature],
                lngLat: event.lngLat
            });
        }
        return true;
    }

    function clearState() {
        var pending = state.getAbortNearby();
        if (pending) pending.abort();
        loadedForLatLng = null;
        state.setNearbyLoaded(false);
        state.setNearbyItems([]);
        state.setActiveNearbyItem(null);
        state.setActiveNearbyId(null);
        activeTabKey = null;
        cachedTabs = [];
        clearVisuals();
        config.nearbyMetaEl.textContent = 'Wybierz obiekt i otwórz otoczenie';
        if (state.getActiveTab() === 'nearby') {
            config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>Brak aktywnego punktu odniesienia</strong><span>Najpierw wybierz transakcję, działkę albo budynek.</span></div>';
        }
    }

    function getGroupLabel(item) {
        return item.group_label || item.category_label || item.layer_label || BDOT10K_LAYER_LABELS[item.layer_code] || 'Baza obiektów topograficznych';
    }

    function getCardTopLabel(item, groupLabel) {
        var topLabel = item.layer_label || item.category_label || BDOT10K_LAYER_LABELS[item.layer_code] || null;
        if (topLabel && topLabel === groupLabel) {
            topLabel = item.impact_classification_label || null;
        }

        return topLabel || 'Otoczenie';
    }

    function infoChip(label, value, popup, chipClass) {
        return '<span class="nm-chip ' + (chipClass || '') + '">'
            + (label ? '<b>' + esc(label) + '</b> ' : '')
            + esc(value)
            + renderInfoHelpButton(popup, 'Wyjaśnienie: ' + (label || value))
            + '</span>';
    }

    function buildCategoryTabs(nearbyItems, activeNearbyId) {
        return buildNearbyFamilies(nearbyItems, activeNearbyId);
    }

    function getTabForItem(item) {
        return getNearbyFamilyKey(item);
    }

    function renderCardHtml(item, index, activeNearbyId, groupLabel) {
        var isActive = activeNearbyId != null && String(item.bdot10k_id) === String(activeNearbyId);
        var impactSummary = bdot10kImpactSummary(item);
        var spatialSummary = bdot10kSpatialImpactSummary(item);
        var balanceLabel = bdot10kImpactBalanceLabel(item);
        var impactChipClass = item.negative ? 'nm-chip--danger' : (item.impact_classification === 'useful' ? 'nm-chip--success' : '');
        var impactTriggerChipClass = item.impact_trigger === 'cluster' ? 'nm-chip--info' : '';
        var topLabel = getCardTopLabel(item, groupLabel);
        var help = bdot10kImpactHelp(item);
        var layerLabel = bdot10kLayerLabel(item);

        var cardAccent = item.negative ? 'nm-nearby-card--danger' : 'nm-nearby-card--success';
        return '<article class="ms-card ms-card--grid nm-nearby-card ' + cardAccent + (isActive ? ' is-active' : '') + '">' +
            '<button class="nm-nearby-card-select" type="button" data-nearby-index="' + index + '" aria-label="Pokaż na mapie: ' + escAttr(item.label) + '">' +
                '<div class="nm-nearby-card-top">' +
                    '<span class="nm-pill">' + esc(topLabel) + '</span>' +
                    '<span class="nm-chip ' + (item.negative ? 'nm-chip--danger' : 'nm-chip--success') + '">' + esc(formatDist(item.dist_m)) + '</span>' +
                '</div>' +
                '<div class="nm-list-address">' + esc(item.label) + '</div>' +
                (item.airport_corridor ? buildAirportCorridorNoteHtml(item.airport_corridor, esc) : '') +
                (item.may_not_exist ? '<div class="nm-building-gone-notice"><span class="nm-building-gone-icon" aria-hidden="true">&#9888;</span> ' + esc(getMessage('buildingMayNotExist', 'Budynek może już nie istnieć')) + '</div>' : '') +
                ((impactSummary || spatialSummary) ? '<div class="nm-card-subline">' + esc(impactSummary || spatialSummary) + '</div>' : '') +
            '</button>' +
            '<div class="nm-nearby-meta-row">' +
                (layerLabel && layerLabel !== groupLabel ? '<span class="nm-chip">' + esc(layerLabel) + '</span>' : '') +
                (item.impact_classification_label
                    ? infoChip('', item.impact_classification_label, help.classification, impactChipClass)
                    : (item.negative ? '<span class="nm-chip nm-chip--danger">Potencjalna uciążliwość</span>' : '<span class="nm-chip nm-chip--success">' + esc(getMessage('nearbyLabel', 'Otoczenie')) + '</span>')) +
                (item.impact_trigger_label
                    ? infoChip('', item.impact_trigger_label,
                        item.impact_trigger === 'cluster' ? help.clusterTrigger
                            : (item.impact_trigger === 'corridor' ? help.corridorTrigger : help.directTrigger),
                        impactTriggerChipClass)
                    : '') +
                (item.nuisance_level != null ? infoChip('Uciążliwość', item.nuisance_level + ' na 5', help.nuisance) : '') +
                (item.usefulness_level != null ? infoChip('Użyteczność', item.usefulness_level + ' na 5', help.usefulness) : '') +
                (item.impact_radius != null ? infoChip('Promień wpływu', formatDist(item.impact_radius), help.radius) : '') +
                (item.cluster_threshold > 1
                    ? infoChip('Skupisko', (item.same_type_count || 0) + ' z ' + item.cluster_threshold, help.cluster)
                    : '') +
                (balanceLabel ? infoChip('Bilans wpływu', balanceLabel, help.balance) : '') +
            '</div>' +
            '<div class="nm-nearby-card-actions">' +
                (item.detail_url ? '<a class="ms-link nm-ext-link nm-ext-link--compact" href="' + escAttr(item.detail_url) + '" target="_blank" rel="noopener">Szczegóły ↗</a>' : '') +
                (item.center ? '<a class="ms-link nm-ext-link nm-ext-link--compact" href="' + geoportalMapviewUrl(item.center.lat, item.center.lng) + '" target="_blank" rel="noopener">Geoportal ↗</a>' : '') +
            '</div>' +
        '</article>';
    }

    function renderFamilyHtml(tab, activeNearbyId) {
        if (!tab || !tab.entries.length) return '';
        var tabIndex = cachedTabs.findIndex(function(entry) { return entry.key === tab.key; });
        var previousTab = tabIndex > 0 ? cachedTabs[tabIndex - 1] : null;
        var nextTab = tabIndex >= 0 && tabIndex < cachedTabs.length - 1 ? cachedTabs[tabIndex + 1] : null;
        var representative = tab.representative || tab.entries[0].item;
        var representativeHelp = bdot10kImpactHelp(representative);
        var radiusRepresentative = tab.entries.reduce(function(current, entry) {
            return Number(entry.item.maximum_impact_radius) > Number(current.maximum_impact_radius) ? entry.item : current;
        }, representative);
        var maximumRadiusHelp = bdot10kImpactHelp(radiusRepresentative).maximumRadius;
        var chipClass = tab.accent === 'danger' ? 'nm-chip--danger' : 'nm-chip--success';
        var nuisance = levelRangeLabel(tab.nuisanceRange);
        var usefulness = levelRangeLabel(tab.usefulnessRange);
        var composition = tab.components.map(function(component) {
            return esc(component.label) + ' <b>' + component.count + '</b>';
        }).join('<span aria-hidden="true">·</span>');
        var cards = tab.entries.map(function(entry) {
            return renderCardHtml(entry.item, entry.index, activeNearbyId, tab.label);
        }).join('');

        return '<div class="nm-nearby-family-stage" data-family-position="' + (tabIndex + 1) + '">' +
            '<section class="nm-nearby-family-panel" id="' + escAttr(tab.panelId) + '" role="tabpanel" aria-labelledby="' + escAttr(tab.tabId) + '">' +
            '<article class="nm-nearby-family-card nm-nearby-family-card--' + tab.accent + '">' +
            '<button class="nm-scroll-arrow nm-scroll-arrow--left" type="button" data-family-nav="previous"' + (previousTab ? ' aria-label="Poprzednia kategoria: ' + escAttr(previousTab.label) + '" title="' + escAttr(previousTab.label) + '"' : ' disabled aria-label="Brak poprzedniej kategorii"') + '><span class="nm-scroll-arrow-icon" aria-hidden="true">‹</span></button>' +
            '<button class="nm-scroll-arrow nm-scroll-arrow--right" type="button" data-family-nav="next"' + (nextTab ? ' aria-label="Następna kategoria: ' + escAttr(nextTab.label) + '" title="' + escAttr(nextTab.label) + '"' : ' disabled aria-label="Brak następnej kategorii"') + '><span class="nm-scroll-arrow-icon" aria-hidden="true">›</span></button>' +
                '<div class="nm-nearby-family-top">' +
                    '<span class="nm-pill">Kategoria otoczenia</span>' +
                    '<span class="nm-chip ' + chipClass + '">' + esc(Number(tab.minDist) < 1 ? 'w tym miejscu' : 'najbliżej ' + formatDist(tab.minDist)) + '</span>' +
                '</div>' +
                '<h3 class="nm-nearby-family-title">' + esc(tab.label) + '</h3>' +
                '<p class="nm-nearby-family-source-count">' + esc(sourceElementsLabel(tab.count)) + ' w Bazie obiektów topograficznych</p>' +
                '<div class="nm-nearby-family-composition" aria-label="Skład elementów źródłowych">' + composition + '</div>' +
                '<div class="nm-nearby-meta-row nm-nearby-family-metrics">' +
                    (representative.impact_classification_label ? infoChip('', representative.impact_classification_label, representativeHelp.classification, chipClass) : '') +
                    (nuisance !== null ? infoChip('Uciążliwość', nuisance + ' na 5', representativeHelp.nuisance) : '') +
                    (usefulness !== null ? infoChip('Użyteczność', usefulness + ' na 5', representativeHelp.usefulness) : '') +
                    (tab.maxImpactRadius !== null ? infoChip('Maksymalny zasięg wpływu', formatDist(tab.maxImpactRadius), maximumRadiusHelp) : '') +
                '</div>' +
                (activeNearbyId != null
                    ? '<button class="nm-nearby-family-show-all" type="button" data-show-nearby-family aria-label="Pokaż na mapie wszystkie obiekty z kategorii ' + escAttr(tab.label) + '"><span aria-hidden="true">&#9678;</span>Pokaż wszystkie na mapie</button>'
                    : '') +
            '</article>' +
            '<details class="nm-nearby-sources"' + (tab.containsActiveItem ? ' open' : '') + '>' +
                '<summary>Pokaż elementy źródłowe <span>' + tab.count + '</span></summary>' +
                '<div class="nm-nearby-source-list">' + cards + '</div>' +
            '</details>' +
            '</section>' +
        '</div>';
    }

    function getActiveTab() {
        if (activeTabKey) {
            var found = cachedTabs.find(function(t) { return t.key === activeTabKey; });
            if (found) return found;
        }
        return cachedTabs.length ? cachedTabs[0] : null;
    }

    function extendBoundsWithCoordinates(bounds, coordinates, mirrorCenter) {
        if (!Array.isArray(coordinates) || coordinates.length === 0) return;
        if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
            if (isFinite(coordinates[0]) && isFinite(coordinates[1])) {
                bounds.extend([coordinates[0], coordinates[1]]);
                if (mirrorCenter) {
                    bounds.extend([
                        (2 * mirrorCenter.lng) - coordinates[0],
                        (2 * mirrorCenter.lat) - coordinates[1]
                    ]);
                }
            }
            return;
        }
        coordinates.forEach(function(child) {
            extendBoundsWithCoordinates(bounds, child, mirrorCenter);
        });
    }

    function extendBoundsWithGeometry(bounds, geometry, mirrorCenter) {
        if (!geometry) return;
        if (Array.isArray(geometry.coordinates)) {
            extendBoundsWithCoordinates(bounds, geometry.coordinates, mirrorCenter);
        }
        if (Array.isArray(geometry.geometries)) {
            geometry.geometries.forEach(function(child) {
                extendBoundsWithGeometry(bounds, child, mirrorCenter);
            });
        }
    }

    function getFamilyMapPadding() {
        var configured = config.getMapPaddingForPanel() || {};
        var padding = {
            top: Number(configured.top) || 0,
            bottom: Number(configured.bottom) || 0,
            left: Number(configured.left) || 0,
            right: Number(configured.right) || 0
        };
        var map = config.getMap();
        var sheetEl = config.nearbyContentEl ? config.nearbyContentEl.closest('.nm-sheet') : null;
        if (!config.isDesktopLayout() || !map || !map.getCanvas || !sheetEl) return padding;

        var canvasRect = map.getCanvas().getBoundingClientRect();
        var sheetRect = sheetEl.getBoundingClientRect();
        var overlapsMap = sheetRect.left < canvasRect.right
            && sheetRect.right > canvasRect.left
            && sheetRect.top < canvasRect.bottom
            && sheetRect.bottom > canvasRect.top;
        if (overlapsMap) {
            var coveredWidth = Math.max(0, canvasRect.right - Math.max(canvasRect.left, sheetRect.left));
            padding.right = Math.max(padding.right, coveredWidth + 20);
        }

        return padding;
    }

    function fitFamilyOnMap(tab) {
        var map = config.getMap();
        if (!map || !tab || !tab.entries.length) return;
        var bounds = new maplibregl.LngLatBounds();
        var selectedLatLng = state.getSelectedLatLng();
        if (selectedLatLng) bounds.extend([selectedLatLng.lng, selectedLatLng.lat]);

        tab.entries.forEach(function(entry) {
            var item = entry.item;
            extendBoundsWithGeometry(bounds, item.geometry, selectedLatLng);
            if (!item.geometry && item.center) {
                bounds.extend([item.center.lng, item.center.lat]);
                if (selectedLatLng) {
                    bounds.extend([
                        (2 * selectedLatLng.lng) - item.center.lng,
                        (2 * selectedLatLng.lat) - item.center.lat
                    ]);
                }
            }
        });

        if (bounds.isEmpty()) return;
        var padding = getFamilyMapPadding();
        var southWest = bounds.getSouthWest();
        var northEast = bounds.getNorthEast();
        if (southWest.lng === northEast.lng && southWest.lat === northEast.lat) {
            map.flyTo({
                center: selectedLatLng ? [selectedLatLng.lng, selectedLatLng.lat] : [southWest.lng, southWest.lat],
                zoom: Math.min(Math.max(config.mapZoom(), OVERLAY_LAYERS_MIN_ZOOM), 17.5),
                duration: 500,
                padding: padding
            });
            return;
        }

        map.fitBounds(bounds, {
            padding: padding,
            maxZoom: 17.5,
            duration: 600
        });
    }

    function navigateFamily(direction, options) {
        var targetKey = resolveAdjacentFamilyKey(cachedTabs, activeTabKey, direction);
        if (!targetKey) return false;
        options = options || {};
        switchTab(targetKey, {
            highlightFamily: true,
            fitFamily: true,
            focusTab: options.focusTab === true
        });
        return true;
    }

    function bindFamilyNavigation(container) {
        if (!container) return;
        container.addEventListener('click', function(e) {
            var showFamilyButton = e.target.closest('[data-show-nearby-family]');
            if (showFamilyButton) {
                switchTab(activeTabKey, { highlightFamily: true, fitFamily: true });
                return;
            }

            var button = e.target.closest('[data-family-nav]');
            if (!button || button.disabled) return;
            navigateFamily(button.getAttribute('data-family-nav'), { focusTab: true });
        });

        var drag = null;
        function resetDrag(animate) {
            if (!drag) return;
            if (drag.preview) drag.preview.remove();
            drag.panel.style.transition = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'transform 180ms ease-out' : 'none';
            drag.panel.style.transform = '';
            drag = null;
        }
        container.addEventListener('touchstart', function(e) {
            resetDrag(false);
            if (config.isDesktopLayout() || e.touches.length !== 1) return;
            if (e.target.closest('.nm-nearby-sources, button, a')) return;
            var panel = e.target.closest('[role="tabpanel"]');
            if (!panel) return;
            drag = { panel: panel, x: e.touches[0].clientX, y: e.touches[0].clientY, dx: 0, horizontal: false };
        }, { passive: true });
        container.addEventListener('touchmove', function(e) {
            if (!drag) return;
            if (e.touches.length !== 1) { resetDrag(false); return; }
            var dx = e.touches[0].clientX - drag.x;
            var dy = e.touches[0].clientY - drag.y;
            if (!drag.horizontal) {
                if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) { resetDrag(false); return; }
                if (Math.abs(dx) <= 8 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
                drag.horizontal = true;
            }
            if (e.cancelable) e.preventDefault();
            drag.dx = dx;
            var direction = dx < 0 ? 1 : -1;
            var tab = getActiveTab();
            var index = cachedTabs.indexOf(tab);
            var adjacent = cachedTabs[index + direction];
            var width = drag.panel.getBoundingClientRect().width + 16;
            var delta = adjacent ? Math.max(-width, Math.min(width, dx)) : dx * 0.2;
            drag.panel.style.animation = 'none';
            drag.panel.style.transition = 'none';
            drag.panel.style.transform = 'translateX(' + delta + 'px)';
            if (drag.previewKey !== (adjacent && adjacent.key)) {
                if (drag.preview) drag.preview.remove();
                drag.preview = null;
                drag.previewKey = adjacent && adjacent.key;
                if (adjacent) {
                    var template = document.createElement('template');
                    template.innerHTML = renderFamilyHtml(adjacent, null);
                    drag.preview = template.content.querySelector('[role="tabpanel"]');
                    drag.preview.removeAttribute('id');
                    drag.preview.removeAttribute('aria-labelledby');
                    drag.preview.querySelectorAll('[id]').forEach(function(el) { el.removeAttribute('id'); });
                    drag.preview.setAttribute('aria-hidden', 'true');
                    drag.preview.setAttribute('inert', '');
                    drag.preview.classList.add('is-swipe-preview');
                    drag.panel.parentElement.appendChild(drag.preview);
                }
            }
            if (drag.preview) drag.preview.style.transform = 'translateX(' + (delta + direction * width) + 'px)';
        }, { passive: false });
        container.addEventListener('touchend', function() {
            if (!drag) return;
            var change = drag.horizontal && Math.abs(drag.dx) >= 48;
            var direction = drag.dx < 0 ? 'next' : 'previous';
            var remaining = (direction === 'next' ? 1 : -1) * (drag.panel.getBoundingClientRect().width + 16) + drag.dx;
            resetDrag(true);
            if (change && navigateFamily(direction) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                var panel = container.querySelector('[role="tabpanel"]');
                if (panel) panel.animate([{ transform: 'translateX(' + remaining + 'px)' }, { transform: 'translateX(0)' }], { duration: 180, easing: 'ease-out' });
            }
        }, { passive: true });
        container.addEventListener('touchcancel', function() { resetDrag(true); }, { passive: true });
    }

    function updateScrollArrows(wrapEl) {
        if (!wrapEl) return;
        var scrollEl = wrapEl.querySelector('.nm-nearby-tabbar');
        if (!scrollEl) return;
        var leftArrow = wrapEl.querySelector('.nm-scroll-arrow--left');
        var rightArrow = wrapEl.querySelector('.nm-scroll-arrow--right');
        if (leftArrow) leftArrow.classList.toggle('is-hidden', scrollEl.scrollLeft <= 2);
        if (rightArrow) rightArrow.classList.toggle('is-hidden', scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 2);
    }

    function bindScrollArrows(wrapEl) {
        if (!wrapEl) return;
        var scrollEl = wrapEl.querySelector('.nm-nearby-tabbar');
        if (!scrollEl) return;

        scrollEl.addEventListener('scroll', function() {
            updateScrollArrows(wrapEl);
        }, { passive: true });

        wrapEl.addEventListener('click', function(e) {
            var arrow = e.target.closest('[data-tabbar-arrow]');
            if (!arrow) return;
            var direction = arrow.getAttribute('data-tabbar-arrow');
            var scrollAmount = direction === 'left' ? -scrollEl.clientWidth * 0.7 : scrollEl.clientWidth * 0.7;
            scrollEl.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        });

        bindMouseDragScroll(scrollEl);
        updateScrollArrows(wrapEl);
    }

    function bindMouseDragScroll(scrollEl) {
        var isDown = false;
        var startX = 0;
        var startScrollLeft = 0;
        var moved = 0;
        var DRAG_THRESHOLD = 5;

        scrollEl.addEventListener('mousedown', function(e) {
            // Only primary button; skip if user is interacting with native controls
            if (e.button !== 0) return;
            isDown = true;
            moved = 0;
            startX = e.clientX;
            startScrollLeft = scrollEl.scrollLeft;
            scrollEl.classList.add('is-dragging');
        });

        document.addEventListener('mousemove', function(e) {
            if (!isDown) return;
            var dx = e.clientX - startX;
            if (Math.abs(dx) > moved) moved = Math.abs(dx);
            if (moved > DRAG_THRESHOLD) {
                e.preventDefault();
                scrollEl.scrollLeft = startScrollLeft - dx;
            }
        });

        function endDrag() {
            if (!isDown) return;
            isDown = false;
            scrollEl.classList.remove('is-dragging');
            if (moved > DRAG_THRESHOLD) {
                // Suppress the click that follows a drag
                var suppress = function(ev) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    scrollEl.removeEventListener('click', suppress, true);
                };
                scrollEl.addEventListener('click', suppress, true);
            }
        }

        document.addEventListener('mouseup', endDrag);
        scrollEl.addEventListener('mouseleave', function() {
            // Don't end drag on leave — let user keep dragging across the document
        });

        // Prevent native image/text drag from interfering
        scrollEl.addEventListener('dragstart', function(e) {
            if (isDown) e.preventDefault();
        });
    }

    function updateActiveCardClass() {
        var container = config.nearbyContentEl.querySelector('.nm-nearby-source-list');
        if (!container) return;
        var activeId = state.getActiveNearbyId();
        var cards = container.querySelectorAll('.nm-nearby-card');
        cards.forEach(function(card) {
            var selectButton = card.querySelector('[data-nearby-index]');
            var index = selectButton ? parseInt(selectButton.getAttribute('data-nearby-index'), 10) : NaN;
            var items = state.getNearbyItems();
            var isActive = activeId != null && !isNaN(index) && items[index] && String(items[index].bdot10k_id) === String(activeId);
            card.classList.toggle('is-active', isActive);
        });
    }

    function renderFamilyContent() {
        var familyContainer = config.nearbyContentEl.querySelector('.nm-nearby-family-container');
        if (!familyContainer) return;

        var tab = getActiveTab();
        var activeNearbyId = state.getActiveNearbyId();
        familyContainer.innerHTML = tab ? renderFamilyHtml(tab, activeNearbyId) : '';
    }

    function switchTab(key, options) {
        options = options || {};
        activeTabKey = key;
        if (options.highlightFamily) {
            state.setActiveNearbyItem(null);
            state.setActiveNearbyId(null);
            clearPowerLineBuffer();
        }

        var tabbarEl = config.nearbyContentEl.querySelector('.nm-nearby-tabbar');
        if (tabbarEl) {
            var tabs = tabbarEl.querySelectorAll('.nm-nearby-tab');
            tabs.forEach(function(el) {
                var isActive = el.getAttribute('data-tab-key') === key;
                el.classList.toggle('is-active', isActive);
                el.setAttribute('aria-selected', isActive ? 'true' : 'false');
                el.setAttribute('tabindex', isActive ? '0' : '-1');
                var tab = cachedTabs.find(function(t) { return t.key === el.getAttribute('data-tab-key'); });
                if (isActive && tab) el.setAttribute('aria-controls', tab.panelId);
                else el.removeAttribute('aria-controls');
            });
        }

        renderFamilyContent();
        scrollActiveTabIntoView();
        if (options.highlightFamily) {
            var selectedFamily = getActiveTab();
            setActiveFamilyHighlight(selectedFamily);
            setFamilyFocusLink(selectedFamily);
            if (options.fitFamily) fitFamilyOnMap(selectedFamily);
            config.syncUrl();
            if (options.focusTab) {
                var activeTabButton = config.nearbyContentEl.querySelector('.nm-nearby-tab.is-active');
                if (activeTabButton) activeTabButton.focus({ preventScroll: true });
            }
        }
    }

    function renderList() {
        var nearbyItems = state.getNearbyItems();
        var activeNearbyId = state.getActiveNearbyId();

        if (!nearbyItems.length) {
            cachedTabs = [];
            config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>Brak obiektów w zasięgu</strong><span>W pobliżu wybranego miejsca nie znaleziono obiektów Bazy obiektów topograficznych dla aktualnych filtrów i promieni wyszukiwania.</span></div>';
            if (config.syncNearbyHalfSnap) config.syncNearbyHalfSnap();
            return;
        }

        cachedTabs = buildCategoryTabs(nearbyItems, activeNearbyId);
        cachedTabs.forEach(function(tab, index) {
            tab.tabId = 'nearby-family-tab-' + index;
            tab.panelId = 'nearby-family-panel-' + index;
        });

        if (!activeTabKey || !cachedTabs.find(function(t) { return t.key === activeTabKey; })) {
            var activeTab = cachedTabs.find(function(t) { return t.containsActiveItem; });
            activeTabKey = activeTab ? activeTab.key : (cachedTabs.length ? cachedTabs[0].key : null);
        }

        var tabBarArrowLeft = '<button class="nm-scroll-arrow nm-scroll-arrow--left is-hidden" type="button" data-tabbar-arrow="left"><span class="nm-scroll-arrow-icon" aria-hidden="true">&#8249;</span></button>';
        var tabBarArrowRight = '<button class="nm-scroll-arrow nm-scroll-arrow--right" type="button" data-tabbar-arrow="right"><span class="nm-scroll-arrow-icon" aria-hidden="true">&#8250;</span></button>';

        var tabsHtml = cachedTabs.map(function(tab) {
            var isActive = tab.key === activeTabKey;
            // Only the selected tab's panel is rendered, so only that tab points at it.
            return '<button id="' + tab.tabId + '" class="nm-nearby-tab nm-nearby-tab--' + tab.accent + (isActive ? ' is-active' : '') + '" type="button" role="tab"' + (isActive ? ' aria-controls="' + tab.panelId + '"' : '') + ' aria-selected="' + (isActive ? 'true' : 'false') + '" tabindex="' + (isActive ? '0' : '-1') + '" data-tab-key="' + escAttr(tab.key) + '" aria-label="' + escAttr(tab.label + ', ' + sourceElementsLabel(tab.count)) + '">' +
                esc(tab.label) +
                '<span class="nm-nearby-tab-count">' + tab.count + '</span>' +
            '</button>';
        }).join('');

        config.nearbyContentEl.innerHTML =
            '<div class="nm-nearby-tabbar-wrap">' +
                tabBarArrowLeft +
                '<div class="nm-nearby-tabbar" role="tablist" aria-label="Kategorie otoczenia">' + tabsHtml + '</div>' +
                tabBarArrowRight +
            '</div>' +
            '<div class="nm-nearby-family-container"></div>';

        var tabbarWrap = config.nearbyContentEl.querySelector('.nm-nearby-tabbar-wrap');
        bindScrollArrows(tabbarWrap);

        config.nearbyContentEl.querySelector('.nm-nearby-tabbar').addEventListener('click', function(e) {
            var tabBtn = e.target.closest('[data-tab-key]');
            if (!tabBtn) return;
            switchTab(tabBtn.getAttribute('data-tab-key'), { highlightFamily: true, fitFamily: true });
        });

        config.nearbyContentEl.querySelector('.nm-nearby-tabbar').addEventListener('keydown', function(e) {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
            var buttons = Array.prototype.slice.call(e.currentTarget.querySelectorAll('[role="tab"]'));
            var currentIndex = buttons.indexOf(e.target.closest('[role="tab"]'));
            if (currentIndex < 0) return;
            e.preventDefault();
            var nextIndex = currentIndex;
            if (e.key === 'Home') nextIndex = 0;
            if (e.key === 'End') nextIndex = buttons.length - 1;
            if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
            if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
            buttons[nextIndex].focus();
            switchTab(buttons[nextIndex].getAttribute('data-tab-key'), { highlightFamily: true, fitFamily: true, focusTab: true });
        });

        bindFamilyNavigation(config.nearbyContentEl.querySelector('.nm-nearby-family-container'));
        renderFamilyContent();
        if (!activeNearbyId) setActiveFamilyHighlight(getActiveTab());
        scrollActiveTabIntoView();
        if (config.syncNearbyHalfSnap) config.syncNearbyHalfSnap();
    }

    function applyLayerVisibility() {
        var map = config.getMap();
        if (!map) return;

        var showNearby = state.getActiveTab() === 'nearby';
        ['lyr-nearby-circle', 'lyr-nearby-fill', 'lyr-nearby-line', 'lyr-nearby-linestring'].concat(HIGHLIGHT_LAYER_IDS, POWER_LINE_BUFFER_LAYER_IDS).forEach(function(id) {
            if (map.getLayer(id)) setVisibility(map, id, showNearby);
        });

        var nearbyLinkLayer = state.getNearbyLinkLayer();
        if (nearbyLinkLayer) {
            if (showNearby) {
                nearbyLinkLayer.markerGroup && nearbyLinkLayer.markerGroup.addTo(map);
            } else {
                nearbyLinkLayer.markerGroup && nearbyLinkLayer.markerGroup.remove();
            }
            ['lyr-nearby-link-casing', 'lyr-nearby-link-line'].forEach(function(id) {
                if (map.getLayer(id)) setVisibility(map, id, showNearby);
            });
        }

        SelectionPinLayer.LAYER_IDS.forEach(function(id) {
            if (map.getLayer(id)) setVisibility(map, id, showNearby);
        });
    }

    function buildFocusLayer(item) {
        var selectedLatLng = state.getSelectedLatLng();
        if (!selectedLatLng || !item) return null;

        var nearestCoordinates = item.geometry ? nearestPointOnGeometry(item.geometry, selectedLatLng) : null;
        var lineTarget = nearestCoordinates
            ? { lng: nearestCoordinates[0], lat: nearestCoordinates[1] }
            : (item.nearest || item.center);
        if (!lineTarget) return null;
        var distMeters = haversineDistance(selectedLatLng, lineTarget);
        var distLabel = formatDist(distMeters);
        var mid = midpointLatLng(selectedLatLng, lineTarget);

        var lineGeoJson = {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: [
                    [selectedLatLng.lng, selectedLatLng.lat],
                    [lineTarget.lng, lineTarget.lat]
                ]
            }
        };

        var map = config.getMap();
        removeSourceAndLayers(map, 'src-nearby-link', ['lyr-nearby-link-casing', 'lyr-nearby-link-line']);
        addOrUpdateGeoJsonSource(map, 'src-nearby-link', { type: 'FeatureCollection', features: [lineGeoJson] });
        map.addLayer({
            id: 'lyr-nearby-link-casing', type: 'line', source: 'src-nearby-link',
            paint: { 'line-color': 'rgba(255,255,255,0.88)', 'line-width': 7, 'line-opacity': 0.95 }
        });
        map.addLayer({
            id: 'lyr-nearby-link-line', type: 'line', source: 'src-nearby-link',
            paint: { 'line-color': '#be185d', 'line-width': 4, 'line-opacity': 0.96, 'line-dasharray': [10, 7] }
        });

        var markerGroup = new MarkerGroup();
        markerGroup.add(createHtmlMarker(
            [selectedLatLng.lng, selectedLatLng.lat],
            '<span class="nm-nearby-origin-label">Wybrany obiekt</span>' +
                '<span class="nm-nearby-origin-pin" aria-hidden="true">' +
                    '<svg viewBox="0 0 42 52" focusable="false">' +
                        '<path d="M21 1.5C10.5 1.5 2 10 2 20.5C2 34.75 21 50 21 50S40 34.75 40 20.5C40 10 31.5 1.5 21 1.5Z" />' +
                        '<circle class="nm-nearby-origin-pin-center" cx="21" cy="20.5" r="10" />' +
                        '<image href="/metroskop-logo.svg" x="13" y="12.5" width="16" height="16" />' +
                    '</svg>' +
                '</span>',
            'nm-nearby-origin-marker',
            { anchor: 'bottom', offset: [0, -3] }
        ));
        if (mid) {
            markerGroup.add(createHtmlMarker(
                [mid.lng, mid.lat],
                '<span class="nm-distance-badge">' + esc(distLabel) + '</span>',
                'nm-distance-marker'
            ));
        }

        return { markerGroup: markerGroup };
    }

    function clearFocusLink() {
        var map = config.getMap();
        var nearbyLinkLayer = state.getNearbyLinkLayer();
        if (!nearbyLinkLayer || !map) return;
        if (nearbyLinkLayer.markerGroup) nearbyLinkLayer.markerGroup.remove();
        removeSourceAndLayers(map, 'src-nearby-link', ['lyr-nearby-link-casing', 'lyr-nearby-link-line']);
        state.setNearbyLinkLayer(null);
    }

    function setFamilyFocusLink(tab) {
        clearFocusLink();
        if (!tab || !tab.entries.length) {
            clearPowerLineBuffer();
            return;
        }

        var map = config.getMap();
        var nearestEntry = nearestFamilyEntry(tab.entries);
        if (!nearestEntry) {
            clearPowerLineBuffer();
            return;
        }
        var nearestItem = nearestEntry.item;
        loadPowerLineBuffer(nearestItem);
        var nearbyLinkLayer = buildFocusLayer(nearestItem);
        state.setNearbyLinkLayer(nearbyLinkLayer);
        if (state.getActiveTab() === 'nearby' && nearbyLinkLayer) {
            nearbyLinkLayer.markerGroup.addTo(map);
        }
        if (nearbyLinkLayer && typeof config.bringDataLayersToFront === 'function') {
            config.bringDataLayersToFront();
        }
    }

    function refreshFocusLink() {
        var activeItem = state.getActiveNearbyItem();
        if (!activeItem) {
            setFamilyFocusLink(getActiveTab());
            return;
        }

        clearFocusLink();
        var map = config.getMap();
        var nearbyLinkLayer = buildFocusLayer(activeItem);
        state.setNearbyLinkLayer(nearbyLinkLayer);
        if (state.getActiveTab() === 'nearby' && nearbyLinkLayer) {
            nearbyLinkLayer.markerGroup.addTo(map);
        }
        if (nearbyLinkLayer && typeof config.bringDataLayersToFront === 'function') {
            config.bringDataLayersToFront();
        }
    }

    function focusItem(item, options) {
        if (!item || (!item.center && !item.nearest)) return;

        options = options || {};
        var shouldFly = options.fly !== false;
        var map = config.getMap();
        var targetZoom = Math.max(config.mapZoom(), OVERLAY_LAYERS_MIN_ZOOM);
        var itemFocus = item.center || item.nearest;

        state.setActiveNearbyItem(item);
        state.setActiveNearbyId(item.bdot10k_id != null ? String(item.bdot10k_id) : null);

        var itemTabKey = getTabForItem(item);
        if (itemTabKey !== activeTabKey) {
            switchTab(itemTabKey);
        } else {
            renderFamilyContent();
        }
        setActiveHighlight(item.bdot10k_id);
        loadPowerLineBuffer(item);
        var sourcesDetails = config.nearbyContentEl.querySelector('.nm-nearby-sources');
        if (sourcesDetails) sourcesDetails.open = true;
        scrollActiveCardIntoView();
        var activeCard = config.nearbyContentEl.querySelector('.nm-nearby-card.is-active .nm-nearby-card-select');
        if (options.focusCard && activeCard) {
            activeCard.focus({ preventScroll: true });
        }

        if (!config.isDesktopLayout() && config.getCurrentSheetSnap() === 'full') {
            config.setSheetSnap('half');
        }

        clearFocusLink();

        var selectedLatLng = state.getSelectedLatLng();
        if (selectedLatLng) {
            var nearbyLinkLayer = buildFocusLayer(item);
            state.setNearbyLinkLayer(nearbyLinkLayer);
            if (state.getActiveTab() === 'nearby' && nearbyLinkLayer) nearbyLinkLayer.markerGroup.addTo(map);
            if (typeof config.bringDataLayersToFront === 'function') {
                config.bringDataLayersToFront();
            }
            if (shouldFly) {
                var bounds = new maplibregl.LngLatBounds();
                bounds.extend([selectedLatLng.lng, selectedLatLng.lat]);
                bounds.extend([itemFocus.lng, itemFocus.lat]);
                map.fitBounds(bounds, {
                    padding: config.getMapPaddingForPanel(),
                    maxZoom: Math.max(targetZoom, 17),
                    duration: 500
                });
            }
        } else if (shouldFly) {
            map.flyTo({ center: [itemFocus.lng, itemFocus.lat], zoom: targetZoom, duration: 450, padding: config.getMapPaddingForPanel() });
        }

        if (options.sync !== false) {
            config.syncUrl(shouldFly ? itemFocus : null, shouldFly ? targetZoom : null);
        }
    }

    function ensureInteractions() {
        if (interactionsBound) return;

        var map = config.getMap();
        registerCursorHandlers(map, POWER_LINE_BUFFER_LAYER_IDS[0]);
        ['lyr-nearby-fill', 'lyr-nearby-line', 'lyr-nearby-linestring', 'lyr-nearby-circle'].forEach(function(layerId) {
            registerCursorHandlers(map, layerId);
            map.on('click', layerId, function(e) {
                if (!e.features || !e.features.length) return;
                if (handlePowerLineBufferOverlap(e, e.features[0])) return;
                config.consumeHandledMapClick(e);
                e.preventDefault();
                if (e.originalEvent && typeof e.originalEvent.stopPropagation === 'function') {
                    e.originalEvent.stopPropagation();
                }
                var id = e.features[0].properties ? e.features[0].properties.bdot10k_id : null;
                var item = state.getNearbyItems().find(function(entry) {
                    return String(entry.bdot10k_id) === String(id);
                });
                if (item) focusItem(item, { fly: false });
            });
        });

        map.on('click', POWER_LINE_BUFFER_LAYER_IDS[0], function(event) {
            if (!event.features || !event.features.length) return;
            handlePowerLineBufferOverlap(event, null);
        });

        interactionsBound = true;
    }

    function activeHighlightFilter(geometryType, activeId) {
        var idExpr = activeId != null && activeId !== '' ? activeId : -1;
        return ['all', ['==', '$type', geometryType], ['==', 'bdot10k_id', idExpr]];
    }

    function scrollHorizontallyToCenter(scrollEl, targetEl) {
        if (!scrollEl || !targetEl) return;
        var elOffset = targetEl.offsetLeft - scrollEl.offsetLeft;
        var targetScrollLeft = elOffset - (scrollEl.clientWidth - targetEl.offsetWidth) / 2;
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, scrollEl.scrollWidth - scrollEl.clientWidth));
        scrollEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    }

    function scrollActiveTabIntoView() {
        if (!config.nearbyContentEl) return;
        var scrollEl = config.nearbyContentEl.querySelector('.nm-nearby-tabbar');
        var targetEl = config.nearbyContentEl.querySelector('.nm-nearby-tab.is-active');
        scrollHorizontallyToCenter(scrollEl, targetEl);
    }

    function scrollActiveCardIntoView() {
        if (!config.nearbyContentEl) return;
        var targetEl = config.nearbyContentEl.querySelector('.nm-nearby-card.is-active');
        if (targetEl) targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function setNearbyContextOpacity(tab, dimAll) {
        var map = config.getMap();
        if (!map) return;
        var familyMatch = tab && tab.familyKey ? [
            'all',
            ['==', ['get', 'family_key'], tab.familyKey],
            ['==', ['get', 'negative'], tab.accent === 'danger']
        ] : null;
        var opacity = function(active, inactive) {
            if (familyMatch) return ['case', familyMatch, active, inactive];
            return dimAll ? inactive : active;
        };
        var layerPaint = [
            ['lyr-nearby-fill', 'fill-opacity', opacity(1, 0.5)],
            ['lyr-nearby-line', 'line-opacity', opacity(1, 0.5)],
            ['lyr-nearby-linestring', 'line-opacity', opacity(0.85, 0.425)],
            ['lyr-nearby-circle', 'circle-opacity', opacity(0.88, 0.44)],
            ['lyr-nearby-circle', 'circle-stroke-opacity', opacity(1, 0.5)]
        ];
        layerPaint.forEach(function(entry) {
            if (map.getLayer(entry[0])) {
                map.setPaintProperty(entry[0], entry[1], entry[2]);
            }
        });
    }

    function setExternalContextDimmed(dimmed) {
        if (dimmed) {
            setNearbyContextOpacity(null, true);
            return;
        }

        setNearbyContextOpacity(state.getActiveNearbyId() ? null : getActiveTab(), false);
    }

    function prepareForExternalHighlight() {
        state.setActiveNearbyItem(null);
        state.setActiveNearbyId(null);
        clearPowerLineBuffer();
        clearFocusLink();
        setActiveHighlight(null);
        setNearbyContextOpacity(null, true);
    }

    function setActiveHighlight(activeId) {
        var map = config.getMap();
        if (!map) return;
        setNearbyContextOpacity(null);
        var parsed = activeId != null && activeId !== '' ? Number(activeId) : -1;
        if (!isFinite(parsed)) parsed = -1;
        var pairs = [
            ['lyr-nearby-highlight-fill', 'Polygon'],
            ['lyr-nearby-highlight-polygon-casing', 'Polygon'],
            ['lyr-nearby-highlight-line', 'Polygon'],
            ['lyr-nearby-highlight-linestring-casing', 'LineString'],
            ['lyr-nearby-highlight-linestring', 'LineString'],
            ['lyr-nearby-highlight-circle', 'Point']
        ];
        pairs.forEach(function(pair) {
            if (map.getLayer(pair[0])) {
                map.setFilter(pair[0], activeHighlightFilter(pair[1], parsed));
            }
        });
        if (parsed > 0) {
            highlightPulsator.start('item');
        } else {
            highlightPulsator.stop();
        }
    }

    function activeFamilyHighlightFilter(geometryType, tab) {
        if (!tab || !tab.familyKey) {
            return ['all', ['==', '$type', geometryType], ['==', 'bdot10k_id', -1]];
        }
        return [
            'all',
            ['==', '$type', geometryType],
            ['==', 'family_key', tab.familyKey],
            ['==', 'negative', tab.accent === 'danger']
        ];
    }

    function setActiveFamilyHighlight(tab) {
        var map = config.getMap();
        if (!map) return;
        highlightPulsator.stop();
        var pairs = [
            ['lyr-nearby-highlight-fill', 'Polygon'],
            ['lyr-nearby-highlight-polygon-casing', 'Polygon'],
            ['lyr-nearby-highlight-line', 'Polygon'],
            ['lyr-nearby-highlight-linestring-casing', 'LineString'],
            ['lyr-nearby-highlight-linestring', 'LineString'],
            ['lyr-nearby-highlight-circle', 'Point']
        ];
        pairs.forEach(function(pair) {
            if (map.getLayer(pair[0])) {
                map.setFilter(pair[0], activeFamilyHighlightFilter(pair[1], tab));
            }
        });
        setNearbyContextOpacity(tab);
        if (tab && tab.familyKey) highlightPulsator.start('family');
    }

    function renderLayer(data) {
        var map = config.getMap();
        var nearbyLayer = state.getNearbyLayer();

        if (nearbyLayer && map) {
            removeSourceAndLayers(map, 'src-nearby', ['lyr-nearby-circle', 'lyr-nearby-fill', 'lyr-nearby-line', 'lyr-nearby-linestring'].concat(HIGHLIGHT_LAYER_IDS));
        }
        state.setNearbyLayer(null);
        if (!data || !data.features || !data.features.length) return;

        addOrUpdateGeoJsonSource(map, 'src-nearby', data);

        map.addLayer({
            id: 'lyr-nearby-fill', type: 'fill', source: 'src-nearby',
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'fill-color': ['case', ['==', ['get', 'negative'], true], 'rgba(220,38,38,0.12)', 'rgba(21,128,61,0.12)'],
                'fill-opacity': 1
            }
        });
        map.addLayer({
            id: 'lyr-nearby-line', type: 'line', source: 'src-nearby',
            filter: ['==', '$type', 'Polygon'],
            paint: {
                'line-color': ['case', ['==', ['get', 'negative'], true], '#dc2626', '#15803d'],
                'line-width': 2
            }
        });
        map.addLayer({
            id: 'lyr-nearby-linestring', type: 'line', source: 'src-nearby',
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-color': ['case', ['==', ['get', 'negative'], true], '#dc2626', '#15803d'],
                'line-width': 4,
                'line-opacity': 0.85
            }
        });
        map.addLayer({
            id: 'lyr-nearby-circle', type: 'circle', source: 'src-nearby',
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-radius': 6,
                'circle-color': ['case', ['==', ['get', 'negative'], true], '#dc2626', '#15803d'],
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 1,
                'circle-opacity': 0.88
            }
        });

        map.addLayer({
            id: 'lyr-nearby-highlight-fill', type: 'fill', source: 'src-nearby',
            filter: activeHighlightFilter('Polygon', -1),
            paint: getMapHighlightPaint('fill')
        });
        map.addLayer({
            id: 'lyr-nearby-highlight-polygon-casing', type: 'line', source: 'src-nearby',
            filter: activeHighlightFilter('Polygon', -1),
            paint: getMapHighlightPaint('polygonCasing')
        });
        map.addLayer({
            id: 'lyr-nearby-highlight-line', type: 'line', source: 'src-nearby',
            filter: activeHighlightFilter('Polygon', -1),
            paint: getMapHighlightPaint('polygonLine')
        });
        map.addLayer({
            id: 'lyr-nearby-highlight-linestring-casing', type: 'line', source: 'src-nearby',
            filter: activeHighlightFilter('LineString', -1),
            paint: getMapHighlightPaint('lineStringCasing')
        });
        map.addLayer({
            id: 'lyr-nearby-highlight-linestring', type: 'line', source: 'src-nearby',
            filter: activeHighlightFilter('LineString', -1),
            paint: getMapHighlightPaint('lineString')
        });
        map.addLayer({
            id: 'lyr-nearby-highlight-circle', type: 'circle', source: 'src-nearby',
            filter: activeHighlightFilter('Point', -1),
            paint: getMapHighlightPaint('circle')
        });

        state.setNearbyLayer('src-nearby');
        ensureInteractions();
        if (state.getActiveNearbyId()) {
            setActiveHighlight(state.getActiveNearbyId());
        } else {
            var activeFamily = getActiveTab();
            setActiveFamilyHighlight(activeFamily);
            setFamilyFocusLink(activeFamily);
        }
        applyLayerVisibility();
        if (typeof config.bringDataLayersToFront === 'function') {
            config.bringDataLayersToFront();
        }
    }

    function loadForSelection(openTab) {
        var selectedLatLng = state.getSelectedLatLng();
        if (!selectedLatLng) return;
        loadedForLatLng = { lat: selectedLatLng.lat, lng: selectedLatLng.lng };

        clearVisuals();
        activeTabKey = null;
        cachedTabs = [];
        state.setActiveNearbyItem(null);
        state.setNearbyLoaded(true);
        config.nearbyMetaEl.textContent = 'Ładowanie otoczenia…';
        config.nearbyContentEl.innerHTML = '<div class="nm-list-stack" role="status" aria-busy="true">'
            + '<span class="sr-only">Ładowanie otoczenia…</span>'
            + '<div class="ms-card ms-card--grid nm-skeleton-card" aria-hidden="true"><span class="ms-skeleton ms-skeleton--text nm-skeleton-card__badge"></span><span class="ms-skeleton ms-skeleton--title"></span><span class="ms-skeleton ms-skeleton--text"></span><span class="ms-skeleton ms-skeleton--block"></span></div>'
            + '</div>';
        if (openTab !== false) config.setTab('nearby', { snap: 'half', sync: false });
        config.loadSelectionPin();

        var previousAbort = state.getAbortNearby();
        if (previousAbort) previousAbort.abort();

        var controller = new AbortController();
        state.setAbortNearby(controller);

        var queryString = 'lat=' + selectedLatLng.lat.toFixed(6) + '&lng=' + selectedLatLng.lng.toFixed(6);
        if (typeof config.getBdot10kFilterQueryString === 'function') {
            queryString = config.getBdot10kFilterQueryString({
                lat: selectedLatLng.lat.toFixed(6),
                lng: selectedLatLng.lng.toFixed(6),
            });
        }

        fetch('api/bdot10k-nearby?' + queryString, { signal: controller.signal })
            .then(function(response) {
                return response.json().catch(function() { return {}; }).then(function(data) {
                    if (!response.ok) {
                        var error = new Error(data.error || ('HTTP ' + response.status));
                        error.status = response.status;
                        error.data = data;
                        throw error;
                    }
                    return data;
                });
            })
            .then(function(data) {
                var nearbyItems = [];
                if (Array.isArray(data.features)) {
                    data.features.forEach(function(feature) {
                        var props = feature.properties || {};
                        // Corridor polygons are technical map overlays attached to a
                        // runway, not separate nearby objects. Treating one as a card
                        // produced an unnamed "Obiekt" family with "Infinity km".
                        if (props.airport_corridor_zone) return;
                        var center = null;
                        var nearest = null;
                        var edgeDistance = null;
                        if (feature.geometry) {
                            var c = centroidOfGeometry(feature.geometry);
                            if (c) center = { lat: c[1], lng: c[0] };
                            var np = nearestPointOnGeometry(feature.geometry, selectedLatLng);
                            if (np) {
                                nearest = { lat: np[1], lng: np[0] };
                                edgeDistance = haversineDistance(selectedLatLng, nearest);
                            }
                        }
                        nearbyItems.push({
                            bdot10k_id: props.bdot10k_id,
                            detail_url: props.detail_url,
                            label: bdot10kLabel(props),
                            layer_code: props.layer_code,
                            layer_label: props.layer_label,
                            x_kod: props.x_kod,
                            object_label: props.object_label,
                            nazwa: props.nazwa,
                            rodzaj: props.rodzaj,
                            rodzaj_label: props.rodzaj_label,
                            funszcz: props.funszcz,
                            funszcz_label: props.funszcz_label,
                            funszcz_details: props.funszcz_details,
                            group_label: props.group_label,
                            family_key: props.family_key,
                            family_label: props.family_label,
                            family_component_label: props.family_component_label,
                            category_label: props.category_label,
                            impact_classification: props.impact_classification,
                            impact_classification_label: props.impact_classification_label,
                            nuisance_level: props.nuisance_level,
                            usefulness_level: props.usefulness_level,
                            impact_radius: props.impact_radius,
                            maximum_impact_radius: props.cluster_threshold > 1 ? props.cluster_impact_radius : props.impact_radius,
                            cluster_impact_radius: props.cluster_impact_radius,
                            cluster_radius_multiplier: props.cluster_radius_multiplier,
                            cluster_threshold: props.cluster_threshold,
                            same_type_count: props.same_type_count,
                            impact_trigger: props.impact_trigger,
                            impact_trigger_label: props.impact_trigger_label,
                            dist_m: props.dist_m,
                            negative: !!props.negative,
                            may_not_exist: !!props.may_not_exist,
                            airport_corridor: props.airport_corridor || null,
                            geometry: feature.geometry || null,
                            center: center,
                            nearest: nearest,
                            edge_dist_m: edgeDistance
                        });
                    });
                }

                state.setNearbyItems(nearbyItems);
                var familyCount = data.familyCount != null ? Number(data.familyCount) : buildNearbyFamilies(nearbyItems, null).length;
                config.nearbyMetaEl.textContent = nearbyItems.length
                    ? (familyCategoriesLabel(familyCount) + ' · ' + sourceElementsLabel(nearbyItems.length) + (data.isTruncated ? ' · tylko najbliższe' : '') + (config.getHasActiveBdot10kFilters && config.getHasActiveBdot10kFilters() ? ' · po filtrach' : ''))
                    : 'Brak obiektów w pobliżu';

                if (config.getReferenceLabel && config.getReferenceLabel()) config.nearbyMetaEl.textContent = config.getReferenceLabel() + ' · ' + config.nearbyMetaEl.textContent;

                var activeNearbyId = state.getActiveNearbyId();
                if (activeNearbyId) {
                    state.setActiveNearbyItem(nearbyItems.find(function(entry) {
                        return String(entry.bdot10k_id) === String(activeNearbyId);
                    }) || null);
                }

                renderList();
                renderLayer(data);

                var activeNearbyItem = state.getActiveNearbyItem();
                if (activeNearbyItem) {
                    focusItem(activeNearbyItem, { sync: false, fly: true });
                } else if (activeNearbyId) {
                    state.setActiveNearbyId(null);
                    setActiveHighlight(null);
                    renderList();
                }

                config.syncUrl();
            })
            .catch(function(err) {
                if (err.name === 'AbortError') return;
                loadedForLatLng = null;
                console.error('Nearby loading failed', err);
                var isBusy = err.status === 503;
                config.nearbyMetaEl.textContent = isBusy ? 'Analiza otoczenia jest chwilowo zajęta' : 'Nie udało się wczytać otoczenia';
                config.nearbyContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>'
                    + (isBusy ? 'Analiza otoczenia jest chwilowo zajęta' : 'Nie udało się wczytać otoczenia')
                    + '</strong><span>'
                    + (isBusy ? 'Inna analiza jest właśnie wykonywana. Spróbuj ponownie za moment.' : 'Spróbuj ponownie za chwilę.')
                    + '</span><button type="button" class="ms-button ms-button--secondary ms-button--sm" data-nearby-map-retry>Spróbuj ponownie</button></div>';
                var retry = config.nearbyContentEl.querySelector('[data-nearby-map-retry]');
                if (retry) retry.addEventListener('click', function() { loadForSelection(true); });
            })
            .finally(function() {
                if (state.getAbortNearby() === controller) {
                    state.setAbortNearby(null);
                }
            });
    }

    function isLoadedForSelection(latlng) {
        return !!latlng
            && !!loadedForLatLng
            && haversineDistance(latlng, loadedForLatLng) <= 1;
    }

    return {
        applyLayerVisibility: applyLayerVisibility,
        clearState: clearState,
        clearVisuals: clearVisuals,
        focusItem: focusItem,
        isLoadedForSelection: isLoadedForSelection,
        loadForSelection: loadForSelection,
        refreshFocusLink: refreshFocusLink,
        renderLayer: renderLayer,
        renderList: renderList,
        prepareForExternalHighlight: prepareForExternalHighlight,
        setExternalContextDimmed: setExternalContextDimmed,
    };
}
